import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalComponentType } from 'store/reducers/modals/types';
import { closeConfirmationModalAction, openConfirmationModalAction } from 'store/reducers/modals/actions';
import { useAppDispatch } from 'store';
import {
  copyProjectVersionAnotherAction,
  createProjectVersionAnotherAction,
  loadDraftFlowAction,
  loadFlowAction,
  loadShortFlowAction,
} from 'store/reducers/projectManager/actions';
import { useSelector } from 'react-redux';
import { getFlowDraft, getFlowDraftInfo, getFlowsAndDraft } from 'store/reducers/projectManager/getters';
import { unwrapResult } from '@reduxjs/toolkit';
import { FlowType } from 'components/projectsManager/Modals/CopyProjectModal/types';
import { CopyProjectModalComponentUI } from 'modules/ui/ManagerOrAdminComponentsUI/ModalsUI/CopyProjectModalUI';
import Snackbar from 'services/Snackbar';
import { RestoreVersionCallbackProps } from 'modules/ui/ManagerOrAdminComponentsUI/ModalsUI/CopyProjectModalUI/types';

interface CopyProjectModalProps {
  projectId: string;
  flowId: string;
  projectName: string;
}

export const CopyProjectModal: ModalComponentType<CopyProjectModalProps> = ({ onClose, projectId, projectName, flowId }) => {
  const dispatch = useAppDispatch();

  const { flowProjectsList: flowProjectsDraftData } = useSelector(getFlowDraft);
  const flowsAndDraft = useSelector(getFlowsAndDraft);
  const flowDraftInfo = useSelector(getFlowDraftInfo);
  const flowDraftInfoId = flowDraftInfo?.id;
  const defaultSelectFlows = { name: flowDraftInfo?.id || '', value: flowDraftInfo?.name || '' };

  const flowDraftList = flowProjectsDraftData.map(({ id, title }) => ({ name: id, value: title }));
  const [selectFlows, setSelectFlows] = useState<FlowType>(defaultSelectFlows);

  const [flowList, setFlowList] = useState<FlowType[]>([]);
  const selectFlowsMame = selectFlows?.name;
  const selectFlowName = selectFlows?.name;
  const isDraftFlow = selectFlowName === flowDraftInfoId;

  const getFlowData = useCallback(async () => {
    if (!selectFlowName) return;

    try {
      const actionResult = await dispatch(loadShortFlowAction(selectFlowName));
      const unwrappedResult = unwrapResult(actionResult);
      const formattedFlowList = unwrappedResult.map((flow) => ({ name: flow.id, value: flow.title }));
      setFlowList(formattedFlowList);
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectFlowName]);

  useEffect(() => {
    if (!isDraftFlow && selectFlowName) {
      void getFlowData();
    }
  }, [getFlowData, isDraftFlow, projectId, selectFlowName]);

  const onCopyProjectVersionAnother = useCallback(
    async ({
      replaceExistingChecked,
      selectProjectId,
      renameValue,
      runImport,
    }: {
      replaceExistingChecked: boolean;
      selectProjectId?: string;
      renameValue: string;
      runImport: boolean;
    }) => {
      try {
        let actionCopyProjectVersion;
        let actionCreateProjectVersion;

        if (!replaceExistingChecked && selectFlowsMame) {
          actionCreateProjectVersion = await dispatch(
            createProjectVersionAnotherAction({ projectId, name: renameValue, flowId: selectFlowsMame, runImport }),
          ).unwrap();
        }

        if (replaceExistingChecked && selectProjectId) {
          actionCopyProjectVersion = await dispatch(
            copyProjectVersionAnotherAction({ projectFromId: projectId, projectToId: selectProjectId, runImport }),
          ).unwrap();
        }

        if (actionCreateProjectVersion || actionCopyProjectVersion) {
          flowId !== flowDraftInfoId ? await dispatch(loadFlowAction(flowId)) : await dispatch(loadDraftFlowAction());
          dispatch(closeConfirmationModalAction());
          onClose();
        }
      } catch (error) {
        console.error('Error creating project version:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectFlowsMame, projectId, flowId, flowDraftInfoId, onClose],
  );

  const flowsItems = useMemo(
    () => flowsAndDraft?.map((flow) => ({ name: flow?.id || '', value: flow?.name || '' })),
    [flowsAndDraft],
  );
  const projectsItems = useMemo(
    () => (!isDraftFlow && selectFlowName ? flowList : flowDraftList),
    [flowDraftList, flowList, isDraftFlow, selectFlowName],
  );

  const hasName = useCallback((renameValue: string) => projectsItems.find(({ value }) => value === renameValue), [projectsItems]);

  const onRestoreVersionModal = useCallback(
    async ({ renameValue, replaceExistingChecked, selectProject, flowsName, runImport }: RestoreVersionCallbackProps) => {
      if (!flowsName) {
        return Snackbar.show('Поле «Поток» обязательно для заполнения', 'error');
      }

      if (replaceExistingChecked && !selectProject) {
        return Snackbar.show('Поле «Проекты» обязательно для заполнения', 'error');
      }

      if (!replaceExistingChecked && !renameValue) {
        return Snackbar.show('Поле «Название» обязательно для заполнения', 'error');
      }

      if (!replaceExistingChecked && hasName(renameValue)) {
        return Snackbar.show('Проект с таким именем уже существует', 'error');
      }

      if (replaceExistingChecked) {
        dispatch(
          openConfirmationModalAction({
            confirmationButtonText: 'Да',
            cancelButtonText: 'Нет',
            subTitleText: `Точно хотите перезаписать «${selectProject?.value}»?`,
            onConfirm: () =>
              onCopyProjectVersionAnother({
                selectProjectId: selectProject?.name,
                renameValue,
                replaceExistingChecked,
                runImport,
              }),
            titleText: 'Перезапись проекта',
            width: '320px',
            onCancel: () => {},
          }),
        );
      }

      if (!replaceExistingChecked) {
        void (await onCopyProjectVersionAnother({
          selectProjectId: selectProject?.name,
          renameValue,
          replaceExistingChecked,
          runImport,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasName, onCopyProjectVersionAnother],
  );

  return (
    <CopyProjectModalComponentUI
      titleFlow="В поток"
      projectName={projectName}
      onClose={onClose}
      flowsItems={flowsItems}
      projectsItems={projectsItems}
      onSelectFlows={setSelectFlows}
      flows={selectFlows}
      onRestoreVersionModal={onRestoreVersionModal}
    />
  );
};
